import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import {
  Avatar,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import {
  FP,
  PageHeader,
  HeroSearch,
  StatusPill,
  RazonCancelacionTexto,
} from "components/financepro";
import { useApi } from "lib/api";

const SECTION_META = {
  facturacion: { title: 'Facturación', icon: ReceiptLongRoundedIcon, color: FP.color.accentBlue },
  ventasxmayor: { title: 'Ventas x mayor', icon: StorefrontRoundedIcon, color: FP.color.accentBlue },
  incautos: { title: 'Incautos', icon: GavelRoundedIcon, color: FP.color.danger },
  matriculas: { title: 'Matrículas', icon: BadgeRoundedIcon, color: FP.color.info },
  traspasos: { title: 'Traspasos', icon: SwapHorizRoundedIcon, color: FP.color.warning },
  transferencias: { title: 'Transferencias', icon: CompareArrowsRoundedIcon, color: FP.color.successDark },
  gastos: { title: 'Gastos', icon: PaidRoundedIcon, color: FP.color.navy },
};

function SectionCard({ kind, items, renderSecondary }) {
  const meta = SECTION_META[kind];
  const Icon = meta.icon;
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.4,
          py: 1.6,
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: `${meta.color}14`,
            color: meta.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
          {meta.title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <StatusPill label={`${items.length}`} variant="neutral" />
      </Box>
      <List
        sx={{
          maxHeight: 320,
          overflowY: 'auto',
          py: 0.5,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#E2E8F0', borderRadius: 10 },
        }}
      >
        {items.map((item, idx) => (
          <React.Fragment key={`${kind}-${idx}-${item.fecha || idx}`}>
            <ListItem alignItems="flex-start" sx={{ py: 1.1, px: 2 }}>
              <ListItemAvatar sx={{ minWidth: 50 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: `${meta.color}1A`,
                    color: meta.color,
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={item.fecha}
                primaryTypographyProps={{
                  sx: {
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: FP.color.textSecondary,
                    letterSpacing: '0.04em',
                  },
                }}
                secondary={renderSecondary(item)}
                secondaryTypographyProps={{
                  component: 'div',
                  sx: {
                    color: FP.color.textPrimary,
                    fontSize: '0.9rem',
                    lineHeight: 1.55,
                    mt: 0.3,
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  },
                }}
              />
            </ListItem>
            {idx < items.length - 1 && <Divider component="li" sx={{ borderColor: FP.color.border }} />}
          </React.Fragment>
        ))}
      </List>
    </Card>
  );
}

function StatBox({ label, value, accent = '#FFFFFF' }) {
  return (
    <Box
      sx={{
        flex: 1,
        textAlign: 'center',
        py: 2,
        px: 2,
        borderRight: `1px solid rgba(255,255,255,0.08)`,
        '&:last-of-type': { borderRight: 'none' },
      }}
    >
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.6)',
          fontSize: '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: accent,
          fontSize: { xs: '1.05rem', md: '1.25rem' },
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography
        sx={{
          color: FP.color.textSecondary,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: FP.color.textPrimary,
          fontSize: '0.96rem',
          fontWeight: 600,
          wordBreak: 'break-word',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function HistorialChasis({ chasisInicial } = {}) {
  // El chasis puede venir como prop (embebido en el expediente) o por URL
  // (/inventario/historialchasis/:chasis, desde el buscador global).
  const { chasis: chasisUrl } = useParams();
  const chasisPre = chasisInicial || chasisUrl;
  const [motores, setMotores] = useState([])
  const [general, setGeneral] = useState([])
  const [pagos, setPagos] = useState([])
  const [facturacion, setFacturacion] = useState([])
  const [ventasxmayor, setVentasxmayor] = useState([])
  const [incautos, setIncautos] = useState([])
  const [matriculas, setMatriculas] = useState([])
  const [traspasos, setTraspasos] = useState([])
  const [transferencias, setTransferencias] = useState([])
  const [gastos, setGastos] = useState([])
  const [, setRows] = useState([])
  const [chasis, setChasis] = useState("")

  const { post } = useApi()

  useEffect(() => {
    post('/api/listartodomotores').then(({ status, data }) => {
      if (status === 200) {
        setMotores(data["motor"])
      } else if (status === 400) {
        setRows([])
        alert(data["Error"])
      }
    })
  }, []);

  // Preselección: al abrir desde el expediente o el buscador global ya viene el chasis.
  useEffect(() => {
    if (chasisPre) {
      setChasis({ label: String(chasisPre) });
      buscarChasis(String(chasisPre));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chasisPre]);

  async function buscarChasis(labelInicial) {
    const label = typeof labelInicial === "string" ? labelInicial : chasis["label"];
    const { status, data } = await post('/api/historialmotor', { 'chasis': label })
    if (status === 200) {
      setGeneral(data["general"])
      setPagos(data["pagos"])
      setFacturacion(data["facturacion"])
      setVentasxmayor(data["ventasxmayor"] || [])
      setIncautos(data["incautos"])
      setMatriculas(data["matriculas"])
      setTraspasos(data["traspasos"])
      setTransferencias(data["transferencias"])
      setGastos(data["gastos"])
      console.log(data['gastos'])
    } else if (status === 400) {
      setRows([])
      alert(data["Error"])
    }
  }

  const totalGastos = gastos.reduce((acc, item) => acc + item.valor, 0);
  const totalRecibido = pagos[0]?.['valor'] ?? 0;
  const matriculaDisponible = general[0]?.['inventario'] === 'S';

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        width: '100%',
        boxSizing: 'border-box',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Motores"
        subtitle="Buscador de Chasis"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={general.length === 0}
            sx={{
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <HeroSearch
          title="Buscador Global de Chasis"
          description="Ingrese el número de chasis (VIN) para recuperar el historial completo de la unidad, estatus financiero y registro de propietarios."
        >
          <Autocomplete
            disablePortal
            id="historial-chasis-autocomplete"
            options={motores}
            value={chasis || null}
            onChange={(event, value) => setChasis(value)}
            sx={{ flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ej: 1HD1KRE12LB50…"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Box sx={{ pl: 1, pr: 0.5, display: 'flex', alignItems: 'center', color: FP.color.textMuted }}>
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </Box>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 50,
                    borderRadius: 2,
                    bgcolor: '#FFFFFF',
                    fontSize: '0.95rem',
                    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                    '& fieldset': { borderColor: FP.color.border },
                    '&:hover fieldset': { borderColor: FP.color.borderStrong },
                    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
                  },
                }}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={buscarChasis}
            sx={{
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              height: 50,
              minWidth: 140,
              borderRadius: 2,
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Buscar
          </Button>
        </HeroSearch>

        {general.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={8}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.cardBg,
                  boxShadow: FP.shadow.soft,
                  overflow: 'hidden',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    px: 2.6,
                    py: 1.6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${FP.color.border}`,
                    bgcolor: '#FAFBFE',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.02rem' }}>
                    Detalles de la Unidad
                  </Typography>
                  <StatusPill
                    label={matriculaDisponible ? 'Activo / Matrícula disponible' : 'Sin matrícula'}
                    variant={matriculaDisponible ? 'success' : 'danger'}
                  />
                </Box>

                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={5} md={4}>
                      <Box
                        sx={{
                          width: '100%',
                          aspectRatio: '4 / 3',
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${FP.color.navy} 0%, ${FP.color.navyMuted} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <HistoryIcon sx={{ fontSize: 56, opacity: 0.85 }} />
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.7)',
                            fontWeight: 700,
                          }}
                        >
                          Vista de Unidad
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={7} md={8}>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                          <DetailField label="Chasis" value={general[0]['chasis']} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DetailField label="Sucursal" value={general[0]['sucursal']} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DetailField label="Marca" value={general[0]['marca']} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DetailField label="Modelo" value={general[0]['modelo']} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DetailField label="Color" value={general[0]['colore']} />
                        </Grid>
                        {general[0]['suplidor_descrip'] != null && (
                          <Grid item xs={12} sm={6}>
                            <DetailField label="Suplidor" value={general[0]['suplidor_descrip']} />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>

                <Box
                  sx={{
                    background: `linear-gradient(180deg, ${FP.color.navy} 0%, ${FP.color.navyDeep} 100%)`,
                    color: '#FFFFFF',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <StatBox
                    label="Total Gastos"
                    value={`$${totalGastos.toFixed(2)}`}
                    accent="#FFFFFF"
                  />
                  <StatBox
                    label="Total Recibido"
                    value={`$${(typeof totalRecibido === 'number'
                      ? totalRecibido.toFixed(2)
                      : (totalRecibido || '0.00'))}`}
                    accent="#FFFFFF"
                  />
                  <StatBox
                    label="Eventos Registrados"
                    value={String(
                      facturacion.length + ventasxmayor.length + incautos.length + matriculas.length +
                      traspasos.length + transferencias.length + gastos.length
                    )}
                    accent="#FFFFFF"
                  />
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${FP.color.border}`,
                  bgcolor: FP.color.cardBg,
                  boxShadow: FP.shadow.soft,
                  overflow: 'hidden',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    px: 2.4,
                    py: 1.6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${FP.color.border}`,
                    bgcolor: '#FAFBFE',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                    Resumen Financiero
                  </Typography>
                </Box>
                <CardContent sx={{ p: 2.4 }}>
                  <Box sx={{ display: 'grid', rowGap: 1.6 }}>
                    <DetailField
                      label="Total recibido"
                      value={`$${typeof totalRecibido === 'number'
                        ? totalRecibido.toFixed(2)
                        : (totalRecibido || '0.00')}`}
                    />
                    <Divider sx={{ borderColor: FP.color.border }} />
                    <DetailField label="Total gastos" value={`$${totalGastos.toFixed(2)}`} />
                    <Divider sx={{ borderColor: FP.color.border }} />
                    <DetailField
                      label="Eventos en historial"
                      value={String(
                        facturacion.length + ventasxmayor.length + incautos.length + matriculas.length +
                        traspasos.length + transferencias.length + gastos.length
                      )}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  background: `linear-gradient(160deg, ${FP.color.navy} 0%, ${FP.color.navyDeep} 100%)`,
                  color: '#FFFFFF',
                  boxShadow: FP.shadow.navy,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(400px 200px at 100% 0%, rgba(27,108,251,0.25), transparent 60%)',
                    pointerEvents: 'none',
                  }}
                />
                <CardContent sx={{ p: 2.6, position: 'relative' }}>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      mb: 0.6,
                    }}
                  >
                    Estado de la Unidad
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.6 }}>
                    <Typography sx={{ fontSize: '2.4rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em' }}>
                      {matriculaDisponible ? 'Activa' : 'Inactiva'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 999,
                      bgcolor: 'rgba(255,255,255,0.12)',
                      overflow: 'hidden',
                      mb: 1.4,
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: matriculaDisponible ? '92%' : '38%',
                        background: matriculaDisponible
                          ? `linear-gradient(90deg, ${FP.color.success}, #34D399)`
                          : `linear-gradient(90deg, ${FP.color.danger}, #FB7185)`,
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.84rem', lineHeight: 1.6 }}>
                    {matriculaDisponible
                      ? 'La unidad mantiene su matrícula disponible y está apta para operaciones financieras.'
                      : 'La unidad no presenta matrícula disponible en inventario en este momento.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {(facturacion.length > 0 ||
          ventasxmayor.length > 0 ||
          incautos.length > 0 ||
          matriculas.length > 0 ||
          traspasos.length > 0 ||
          transferencias.length > 0 ||
          gastos.length > 0) && (
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: FP.color.textPrimary,
                  fontSize: '1.05rem',
                  letterSpacing: '-0.01em',
                  mb: 2,
                }}
              >
                Historial de Propietarios y Deudores
              </Typography>
              <Grid container spacing={3}>
                {facturacion.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="facturacion"
                      items={facturacion}
                      renderSecondary={(item) => (
                        <>
                          <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                            {item.nombres}
                          </Box>
                          Inicial de ${item.inicial}, financió ${item.financia} · tipo de venta {item.id == 0 ? 'Contado' : 'Crédito'}
                          {Number(item.anulada) === 1 && (
                            <Box sx={{ mt: 0.6 }}>
                              <StatusPill label="Anulada" variant="danger" />
                              <RazonCancelacionTexto razon={item.razonanula} sx={{ mt: 0.4 }} />
                            </Box>
                          )}
                        </>
                      )}
                    />
                  </Grid>
                )}
                {ventasxmayor.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="ventasxmayor"
                      items={ventasxmayor}
                      renderSecondary={(item) => {
                        const simbolo = item.moneda === 'U' ? 'US$' : '$';
                        const documento = item.consignado === 'S' ? 'Conduce' : 'Factura';
                        return (
                          <>
                            <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                              {item.nombres || 'Cliente sin nombre'}
                            </Box>
                            {documento} x mayor #{item.id} · precio {simbolo}{item.precio}
                            {item.tipofactura ? ` · ${item.tipofactura}` : ''}
                            {item.creado ? ` · por ${item.creado}` : ''}
                            {(Number(item.anulada) === 1 || item.consignado === 'D' || item.status === 'C') && (
                              <Box sx={{ mt: 0.6 }}>
                                {Number(item.anulada) === 1 ? (
                                  <>
                                    <StatusPill label="Anulada" variant="danger" />
                                    <RazonCancelacionTexto razon={item.razonanula} sx={{ mt: 0.4 }} />
                                  </>
                                ) : item.consignado === 'D' ? (
                                  <StatusPill label="Devuelto" variant="warning" />
                                ) : (
                                  <StatusPill label="Saldada" variant="success" />
                                )}
                              </Box>
                            )}
                          </>
                        );
                      }}
                    />
                  </Grid>
                )}
                {incautos.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="incautos"
                      items={incautos}
                      renderSecondary={(item) => (
                        <>
                          <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                            {item.nombres}
                          </Box>
                          Le fue incautado por ${item.valor} · estado {item.estado}
                        </>
                      )}
                    />
                  </Grid>
                )}
                {matriculas.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="matriculas"
                      items={matriculas}
                      renderSecondary={(item) => (
                        <>
                          <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                            {item.nombres}
                          </Box>
                          Pagó ${item.valor} por concepto de matrícula
                        </>
                      )}
                    />
                  </Grid>
                )}
                {traspasos.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="traspasos"
                      items={traspasos}
                      renderSecondary={(item) => (
                        <>
                          <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                            {item.nombres}
                          </Box>
                          Le traspasó el motor a {item.tnombres} por un valor de ${item.valor}
                        </>
                      )}
                    />
                  </Grid>
                )}
                {transferencias.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="transferencias"
                      items={transferencias}
                      renderSecondary={(item) => (
                        <>
                          <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                            {item.transmuser} en {item.stransmisor.split('-')[1]}
                          </Box>
                          Transfirió a {item.sreceptor.split('-')[1]} · recibido por {item.receptoruser}
                        </>
                      )}
                    />
                  </Grid>
                )}
                {gastos.length > 0 && (
                  <Grid item xs={12} md={6} lg={4}>
                    <SectionCard
                      kind="gastos"
                      items={gastos}
                      renderSecondary={(item) => (
                        <>
                          <Box sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 0.4 }}>
                            {item.user}
                          </Box>
                          {item.descripcion} · ${item.valor}
                        </>
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
      </Box>
    </Box>
  );
}
